import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { holeMerkliste, aufgeraeumt, MERK_KEY } from '../../fairwaybid/merkliste';

/**
 * FairwayBid – die Merkliste laden
 *
 * Gespeichert sind nur Kennungen. Jede wird einzeln geladen, weil die
 * Marketplace-API keine Abfrage nach einer Liste von Kennungen kennt – und
 * weil einzeln geladen genau das liefert, was die Liste braucht: Wer nicht
 * mehr antwortet, ist gelöscht, und wer geschlossen antwortet, ist vergeben.
 *
 * Deshalb wird jeder Fehlschlag hier nicht zum Fehler der Seite, sondern zur
 * Auskunft über einen Eintrag: Er fällt heraus. Nur wenn gar nichts geht,
 * sieht der Händler eine Fehlermeldung.
 *
 * Was herausgefallen ist, wird anschließend aus dem Profil entfernt. Nicht,
 * um aufzuräumen, sondern damit die Liste beim nächsten Öffnen nicht wieder
 * dreißig Anfragen abfragt, die es nicht mehr gibt.
 */

const ladeEintraege = createAsyncThunk(
  'MerklistePage/lade',
  async (params, { extra: sdk, rejectWithValue }) => {
    const { ids, bildParams } = params;
    if (ids.length === 0) return { listings: [], verfallen: [] };

    const antworten = await Promise.all(
      ids.map(id =>
        sdk.listings
          .show({ id, include: ['author', 'images'], ...bildParams })
          .then(r => denormalisedResponseEntities(r)[0] || null)
          // Gelöscht, geschlossen, kein Zugriff: alles derselbe Ausgang.
          .catch(() => null)
      )
    );

    const geladen = antworten.filter(Boolean);
    // Keine einzige Antwort, obwohl etwas gemerkt ist: Das ist kein
    // aufgeraeumter Eintrag, das ist eine Störung.
    if (geladen.length === 0 && ids.length > 0) {
      const alleWeg = antworten.every(a => a === null);
      if (alleWeg && ids.length > 2) {
        return rejectWithValue(storableError(new Error('Merkliste konnte nicht geladen werden')));
      }
    }

    const { offen, verfallen } = aufgeraeumt(ids, geladen);
    return { listings: offen, verfallen };
  }
);

/** Entfernt verfallene Kennungen aus dem Profil – still, ohne Meldung. */
const entferneVerfallene = createAsyncThunk(
  'MerklistePage/aufraeumen',
  async (bleibt, { extra: sdk, dispatch }) => {
    await sdk.currentUser.updateProfile({ privateData: { [MERK_KEY]: bleibt } });
    await dispatch(fetchCurrentUser());
    return bleibt;
  }
);

const slice = createSlice({
  name: 'MerklistePage',
  initialState: {
    listings: [],
    ladeVorgang: false,
    ladeFehler: null,
    entfernt: 0,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(ladeEintraege.pending, state => {
        state.ladeVorgang = true;
        state.ladeFehler = null;
      })
      .addCase(ladeEintraege.fulfilled, (state, action) => {
        state.ladeVorgang = false;
        state.listings = action.payload.listings;
        state.entfernt = action.payload.verfallen.length;
      })
      .addCase(ladeEintraege.rejected, (state, action) => {
        state.ladeVorgang = false;
        state.ladeFehler = action.payload || storableError(new Error('Unbekannter Fehler'));
      });
  },
});

export const loadData = (params, search, config) => async (dispatch, getState) => {
  // Den Nutzer aus dem Store lesen statt aus der Antwort: Der Store haelt die
  // entnormalisierte Entity, und genau die erwartet holeMerkliste. Welche Form
  // der Thunk zurueckgibt, geht uns hier nichts an.
  await dispatch(fetchCurrentUser());
  const ids = holeMerkliste(getState()?.user?.currentUser);

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  const bildParams = {
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  const ergebnis = await dispatch(ladeEintraege({ ids, bildParams }));
  const verfallen = ergebnis?.payload?.verfallen || [];
  if (verfallen.length > 0) {
    const bleibt = ids.filter(id => !verfallen.includes(id));
    await dispatch(entferneVerfallene(bleibt)).catch(() => null);
  }
  return ergebnis;
};

export default slice.reducer;
